// Travel notes for each experience page — handwritten "tu equipo Xaluca"
// pocket-card content shown as a horizontal slider in the left column of
// the experience hero/story section.
//
// Three to four cards per experience, each with:
//   eyebrow → small uppercase label  (e.g. POR CAPAS · NO LOS OLVIDES)
//   title   → handwritten card title
//   bullets → 3–5 short tips with checkmarks
//   signoff → handwritten valediction, defaults to "— Tu equipo Xaluca"
//   tone    → paper colour key (cream / blue / sand)

const SIGNOFF = {
  en: "— The Xaluca team",
  fr: "— L'équipe Xaluca",
  es: "— Tu equipo Xaluca",
};

const SHARED_SIGNOFF = { signoff: SIGNOFF };

export const EXPERIENCE_TRAVEL_NOTES = {
  desert: [
    {
      ...SHARED_SIGNOFF,
      tone: "cream",
      eyebrow: { en: "Layer up", fr: "Par couches", es: "Por capas" },
      title: {
        en: "Clothes for the Atlas & the desert",
        fr: "Tenues pour l'Atlas & le désert",
        es: "Ropa para el Atlas y el desierto",
      },
      bullets: [
        {
          en: "Dress in layers — a cool High-Atlas morning can become a hot dune afternoon.",
          fr: "Habillez-vous par couches — une matinée fraîche dans le Haut Atlas peut devenir un après-midi chaud sur les dunes.",
          es: "Viste por capas: una mañana fresca en el Alto Atlas puede ser una tarde calurosa en las dunas.",
        },
        {
          en: "A fleece or sweater for the nights — both in the Atlas and the desert it cools sharply after sundown.",
          fr: "Un polaire ou un pull pour les nuits — Atlas comme désert se rafraîchissent dès le coucher du soleil.",
          es: "Un forro polar o jersey para las noches: tanto en el Atlas como en el desierto refresca al caer el sol.",
        },
        {
          en: "Closed, broken-in shoes for kasbahs, gorges and dune-walks.",
          fr: "Des chaussures fermées et déjà rodées pour les kasbahs, les gorges et la marche dans les dunes.",
          es: "Calzado cerrado y ya usado para caminar por kasbahs, gargantas y arena.",
        },
        {
          en: "A wide cheche scarf — shields you from the sun, the wind and the fine sand.",
          fr: "Un grand cheche — il protège du soleil, du vent et du sable fin.",
          es: "Un pañuelo grande (cheche): protege del sol, del viento y de la arena fina.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "sand",
      eyebrow: { en: "Don't forget", fr: "Ne les oubliez pas", es: "No los olvides" },
      title: {
        en: "Essential accessories",
        fr: "Accessoires indispensables",
        es: "Accesorios imprescindibles",
      },
      bullets: [
        {
          en: "Sunglasses and SPF 50 sunblock — Saharan sun is genuinely intense.",
          fr: "Lunettes de soleil et SPF 50 — le soleil saharien est vraiment fort.",
          es: "Gafas de sol y protección solar SPF 50: el sol del Sáhara aprieta de verdad.",
        },
        {
          en: "A cap or wide-brimmed hat for the central hours of the day.",
          fr: "Une casquette ou un chapeau à larges bords pour les heures les plus chaudes.",
          es: "Gorra o sombrero de ala ancha para las horas centrales del día.",
        },
        {
          en: "Lip balm and moisturiser — the air is bone-dry and your skin will notice.",
          fr: "Baume à lèvres et crème hydratante — l'air est très sec et la peau le ressent vite.",
          es: "Crema labial e hidratante: el ambiente es muy seco y la piel lo nota.",
        },
        {
          en: "Reusable water bottle + hand sanitiser for the 4×4 stretches.",
          fr: "Gourde réutilisable + gel hydroalcoolique pour les étapes en 4×4.",
          es: "Botella reutilizable y gel de manos para los trayectos en 4×4.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "blue",
      eyebrow: { en: "After sundown", fr: "Après le coucher", es: "Bajo las estrellas" },
      title: {
        en: "Nights on the route",
        fr: "Nuits sur la route",
        es: "Noches en ruta",
      },
      bullets: [
        {
          en: "A small torch or head-lamp for moving around camp once the lanterns dim.",
          fr: "Une petite lampe frontale ou de poche pour vous déplacer une fois les lanternes baissées.",
          es: "Linterna pequeña o frontal para moverte en el campamento cuando bajan las velas.",
        },
        {
          en: "An extra layer — at 3 a.m. on the dune the temperature can drop by 15 °C.",
          fr: "Une couche supplémentaire — à 3 h du matin sur la dune il peut faire 15 °C de moins.",
          es: "Una capa extra: a las 3 de la mañana en la duna pueden caer 15 °C.",
        },
        {
          en: "Something soft for the ride out — camels sway, you'll thank yourself.",
          fr: "Un vêtement souple pour la traversée — les chameaux balancent, vous nous remercierez.",
          es: "Algo cómodo para el desplazamiento: el camello mece, lo agradecerás.",
        },
        {
          en: "Phone on airplane mode — let the silence and the stars do the talking.",
          fr: "Téléphone en mode avion — laissez le silence et les étoiles parler.",
          es: "Móvil en modo avión: deja que el silencio y las estrellas hablen.",
        },
      ],
    },
  ],

  atlas: [
    {
      ...SHARED_SIGNOFF,
      tone: "cream",
      eyebrow: { en: "Layer up", fr: "Par couches", es: "Por capas" },
      title: {
        en: "Dressed for the High Atlas",
        fr: "Tenue pour le Haut Atlas",
        es: "Vestidos para el Alto Atlas",
      },
      bullets: [
        {
          en: "Layers, always — mornings near the peaks bite, afternoons in the valleys sweat.",
          fr: "Toujours par couches — les matins en altitude piquent, les après-midi en vallée transpirent.",
          es: "Siempre por capas: las mañanas en altura pican, las tardes en los valles sudan.",
        },
        {
          en: "Trekking shoes with grip — the Berber paths are rocky and uneven.",
          fr: "Chaussures de marche avec une bonne adhérence — les sentiers berbères sont rocailleux.",
          es: "Calzado de senderismo con buen agarre: los senderos bereberes son pedregosos.",
        },
        {
          en: "A windbreaker — the wind on a pass at 2,800 m is rarely polite.",
          fr: "Un coupe-vent — le vent sur un col à 2 800 m n'est pas très poli.",
          es: "Un cortavientos: el viento en un puerto a 2 800 m no avisa.",
        },
        {
          en: "Light gloves and a beanie if you're hiking before May.",
          fr: "Gants légers et bonnet si vous randonnez avant mai.",
          es: "Guantes finos y gorro si caminas antes de mayo.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "sand",
      eyebrow: { en: "On the mountain", fr: "Sur la montagne", es: "En la montaña" },
      title: {
        en: "Hike-day essentials",
        fr: "L'essentiel pour la randonnée",
        es: "Imprescindibles del día",
      },
      bullets: [
        {
          en: "Daypack, 1.5 L of water, dried fruit and almonds — the air thins fast.",
          fr: "Petit sac, 1,5 L d'eau, fruits secs et amandes — l'air se raréfie vite.",
          es: "Mochila pequeña, 1,5 L de agua, frutos secos y almendras: el aire se enrarece rápido.",
        },
        {
          en: "Sun protection at altitude is non-negotiable — SPF 50, sunglasses, hat.",
          fr: "Protection solaire en altitude obligatoire — SPF 50, lunettes, chapeau.",
          es: "Protección solar en altura no negociable: SPF 50, gafas, sombrero.",
        },
        {
          en: "Walking poles ease your knees on the long descents.",
          fr: "Des bâtons soulagent les genoux sur les longues descentes.",
          es: "Los bastones de marcha alivian las rodillas en las bajadas largas.",
        },
        {
          en: "A small first-aid kit with blister plasters — your feet will thank you.",
          fr: "Une petite trousse de secours avec pansements pour ampoules — vos pieds vous remercieront.",
          es: "Un pequeño botiquín con tiritas para ampollas: tus pies lo agradecerán.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "blue",
      eyebrow: { en: "After the trail", fr: "Après le sentier", es: "Después del sendero" },
      title: {
        en: "Nights in the valleys",
        fr: "Nuits dans les vallées",
        es: "Noches en los valles",
      },
      bullets: [
        {
          en: "A warm jumper and thick socks — the lakeside refuges drop sharply at night.",
          fr: "Pull chaud et chaussettes épaisses — les refuges au bord du lac chutent vite la nuit.",
          es: "Jersey grueso y calcetines de lana: los refugios junto al lago caen rápido por la noche.",
        },
        {
          en: "Power bank — solar panels keep us warm but won't always charge phones.",
          fr: "Batterie externe — les panneaux solaires nous chauffent mais ne rechargent pas toujours les téléphones.",
          es: "Batería externa: el sol nos calienta, pero no siempre carga los móviles.",
        },
        {
          en: "Earplugs — even silent valleys have a rooster at 5 a.m.",
          fr: "Bouchons d'oreilles — même les vallées les plus silencieuses ont un coq à 5 h.",
          es: "Tapones para los oídos: hasta los valles silenciosos tienen un gallo a las 5.",
        },
      ],
    },
  ],

  kasbah: [
    {
      ...SHARED_SIGNOFF,
      tone: "cream",
      eyebrow: { en: "Modest & breathable", fr: "Sobre & respirant", es: "Sobrio y transpirable" },
      title: {
        en: "Clothes for the medinas",
        fr: "Tenues pour les médinas",
        es: "Ropa para las medinas",
      },
      bullets: [
        {
          en: "Loose, breathable fabrics — linen and cotton survive 40 °C far better than denim.",
          fr: "Tissus amples et respirants — lin et coton supportent les 40 °C bien mieux que le jean.",
          es: "Tejidos amplios y transpirables: lino y algodón aguantan los 40 °C mucho mejor que el vaquero.",
        },
        {
          en: "Shoulders and knees covered when visiting kasbahs, koubbas and small mosques.",
          fr: "Épaules et genoux couverts pour visiter kasbahs, koubbas et petites mosquées.",
          es: "Hombros y rodillas cubiertos al visitar kasbahs, qubbas y mezquitas pequeñas.",
        },
        {
          en: "A light scarf doubles as shawl, headcover and sand-shield.",
          fr: "Un foulard léger sert d'écharpe, de couvre-chef et de protection contre le sable.",
          es: "Un fular ligero hace de chal, cubre-cabeza y barrera contra la arena.",
        },
        {
          en: "Comfortable closed shoes — kasbah stairs are steep and the alleys are uneven.",
          fr: "Chaussures fermées et confortables — les escaliers des kasbahs sont raides, les ruelles irrégulières.",
          es: "Calzado cerrado y cómodo: las escaleras de las kasbahs son empinadas y las callejuelas, irregulares.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "sand",
      eyebrow: { en: "In your daypack", fr: "Dans votre sac", es: "En tu mochila" },
      title: {
        en: "Small things, big difference",
        fr: "Petites choses, grande différence",
        es: "Detalles que marcan",
      },
      bullets: [
        {
          en: "Cash in small bills — souks reward exact change and patient negotiation.",
          fr: "De la monnaie en petits billets — les souks récompensent l'appoint et la négociation patiente.",
          es: "Billetes pequeños: el zoco premia el cambio justo y la negociación tranquila.",
        },
        {
          en: "Refillable bottle — every kasbah we work with has clean drinking water.",
          fr: "Gourde réutilisable — chaque kasbah partenaire dispose d'eau potable.",
          es: "Botella rellenable: todas las kasbahs colaboradoras tienen agua potable.",
        },
        {
          en: "Camera with a wide lens — the courtyards open onto sky, light and tile.",
          fr: "Appareil photo avec un grand-angle — les patios s'ouvrent sur le ciel, la lumière et le zellige.",
          es: "Cámara con angular: los patios se abren al cielo, la luz y el zellige.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "blue",
      eyebrow: { en: "Be a guest", fr: "Soyez un invité", es: "Sé un invitado" },
      title: {
        en: "On etiquette",
        fr: "Sur l'étiquette",
        es: "Sobre la etiqueta",
      },
      bullets: [
        {
          en: "Always ask before photographing people — a smile and a 'shukran' open every door.",
          fr: "Demandez toujours avant de photographier — un sourire et un « shukran » ouvrent toutes les portes.",
          es: "Pide siempre antes de fotografiar — una sonrisa y un «shukran» abren todas las puertas.",
        },
        {
          en: "Eat with your right hand if joining a family meal.",
          fr: "Mangez de la main droite lors d'un repas en famille.",
          es: "Come con la mano derecha si te invitan a una comida familiar.",
        },
        {
          en: "Mint tea is poured three times. Drink all three.",
          fr: "Le thé à la menthe se sert trois fois. Buvez les trois.",
          es: "El té con menta se sirve tres veces. Bébete los tres.",
        },
      ],
    },
  ],

  berber: [
    {
      ...SHARED_SIGNOFF,
      tone: "cream",
      eyebrow: { en: "Be present", fr: "Soyez présent", es: "Sé presente" },
      title: {
        en: "Welcome at the table",
        fr: "À l'accueil de la table",
        es: "Bienvenido a la mesa",
      },
      bullets: [
        {
          en: "Bread is shared, broken not cut — tear off a piece, no knives.",
          fr: "Le pain se partage, on le rompt sans le couper — déchirez un morceau, pas de couteau.",
          es: "El pan se comparte: se parte, no se corta. Arranca un trozo, sin cuchillo.",
        },
        {
          en: "Eat from the slice of the tagine in front of you — never reach across.",
          fr: "Mangez la part du tajine devant vous — ne traversez jamais le plat.",
          es: "Come del trozo de tajine que tienes delante: no cruces el plato.",
        },
        {
          en: "Compliment the cook before reaching for seconds — it's the heart of the meal.",
          fr: "Félicitez la cuisinière avant de vous resservir — c'est le cœur du repas.",
          es: "Felicita a quien cocina antes de repetir: es el corazón de la comida.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "sand",
      eyebrow: { en: "Bring back home", fr: "À ramener", es: "Para llevar a casa" },
      title: {
        en: "Souvenirs that matter",
        fr: "Souvenirs qui comptent",
        es: "Recuerdos con sentido",
      },
      bullets: [
        {
          en: "Hand-woven kilim from a co-op in the Dadès — small, foldable, never out of style.",
          fr: "Kilim tissé à la main d'une coopérative du Dadès — petit, pliable, intemporel.",
          es: "Kilim tejido a mano de una cooperativa del Dadès: pequeño, plegable, nunca pasa de moda.",
        },
        {
          en: "Argan oil pressed in front of you, sealed and dated — accept no substitutes.",
          fr: "Huile d'argan pressée devant vous, scellée et datée — n'acceptez aucun ersatz.",
          es: "Aceite de argán prensado delante de ti, sellado y fechado: no aceptes sustitutos.",
        },
        {
          en: "A small piece of fossilised marble from Erfoud — 400 million years in your suitcase.",
          fr: "Un petit morceau de marbre fossile d'Erfoud — 400 millions d'années dans votre valise.",
          es: "Un trozo pequeño de mármol fósil de Erfoud: 400 millones de años en tu maleta.",
        },
      ],
    },
    {
      ...SHARED_SIGNOFF,
      tone: "blue",
      eyebrow: { en: "Words to learn", fr: "Mots à apprendre", es: "Palabras para aprender" },
      title: {
        en: "Three words go a long way",
        fr: "Trois mots ouvrent les portes",
        es: "Tres palabras abren puertas",
      },
      bullets: [
        {
          en: "Azul — hello in Tamazight, the Berber language of the south.",
          fr: "Azul — bonjour en tamazight, la langue berbère du sud.",
          es: "Azul — hola en tamazight, la lengua bereber del sur.",
        },
        {
          en: "Shukran — thank you, in Darija (Moroccan Arabic).",
          fr: "Shukran — merci, en darija (l'arabe marocain).",
          es: "Shukran — gracias en dariya (el árabe marroquí).",
        },
        {
          en: "Inshallah — 'if God wills'. Used for everything from rain to a 6 a.m. wake-up.",
          fr: "Inshallah — « si Dieu le veut ». Utilisé pour tout, du temps au lever à 6 h.",
          es: "Inshallah — «si Dios quiere». Vale para todo, del tiempo al despertador a las 6.",
        },
      ],
    },
  ],
};

export const getExperienceTravelNotes = (key) =>
  EXPERIENCE_TRAVEL_NOTES[key] || EXPERIENCE_TRAVEL_NOTES.desert;
